import { createClient } from "@supabase/supabase-js";

const url = import.meta.env.VITE_SUPABASE_URL;
const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
export const supabase = (() => {
  if (!url || !key || key.startsWith("sb_secret_")) return null;
  try {
    return createClient(url, key, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false,
      },
    });
  } catch {
    return null;
  }
})();

const messages = {
  P0540: "학생 정보가 변경되었습니다. 목록으로 돌아가 청구 내용을 다시 확인해 주세요.",
  RECIPIENT_CHANGED: "학생 정보가 변경되었습니다. 목록으로 돌아가 청구 내용을 다시 확인해 주세요. 다시 선택할 수 없다면 관리자에게 문의해 주세요.",
  P0535: "청구·납부 기록 또는 확인이 필요한 수업료가 있어 시작일을 변경하지 못했습니다. 해당 월의 납부 내역을 먼저 확인해 주세요.",
  P0536: "수강 시작일은 다른 수업 정보나 금액과 나누어 변경해 주세요.",
  SCHOOL_SEARCH_UNAVAILABLE: "학교 검색 서비스를 이용할 수 없습니다. 잠시 후 다시 검색해 주세요.",
  SCHOOL_SEARCH_THROTTLED: "잠시 후 다시 검색해 주세요.",
  SCHOOL_ADD_FAILED: "학교를 추가하지 못했습니다. 목록을 확인하고 다시 시도해 주세요.",
  AUTH_REQUIRED: "로그인이 필요합니다.",
  AUTH_INVALID: "세션이 만료되었습니다. 다시 로그인해 주세요.",
  ACCOUNT_INACTIVE: "비활성 계정입니다. 관리자에게 문의해 주세요.",
  PROFILE_NOT_FOUND: "직원 정보가 없습니다. 관리자에게 문의해 주세요.",
  FORBIDDEN: "이 작업에 대한 권한이 없습니다.",
  42501: "이 작업에 대한 권한이 없습니다.",
  23505: "이미 등록된 정보입니다.",
  "23P01": "등록 기간이 기존 기록과 겹칩니다.",
  23514: "입력값을 확인해 주세요.",
  22023: "입력값을 확인해 주세요.",
  ACCOUNT_ALREADY_EXISTS: "이미 등록된 계정입니다.",
  INVALID_REQUEST: "입력값을 확인해 주세요.",
  PROVIDER_REQUEST_FAILED:
    "결제 서비스에서 처리하지 못했습니다. 상태를 조회한 후 다시 시도해 주세요.",
};
export function safeError(error, status) {
  const code =
    error?.code ||
    (status === 401
      ? "AUTH_INVALID"
      : status === 403
        ? "FORBIDDEN"
        : "REQUEST_FAILED");
  const result = new Error(
    messages[code] || "요청을 처리하지 못했습니다. 다시 시도해 주세요.",
  );
  result.code = code;
  result.requestId = error?.requestId;
  result.retryable =
    error?.retryable ??
    ![
      "AUTH_REQUIRED",
      "ACCOUNT_INACTIVE",
      "FORBIDDEN",
      "42501",
      "22023",
      "23514",
      "P0535",
      "P0536",
    ].includes(code);
  return result;
}
export function requireClient() {
  if (!supabase)
    throw new Error(
      "Supabase 공개 연결 설정이 필요합니다. 관리자에게 문의해 주세요.",
    );
  return supabase;
}

export function hasAuthInviteCallback(location = window.location) {
  const query = new URLSearchParams(location.search);
  const hash = new URLSearchParams(location.hash.replace(/^#/, ""));
  return query.has("code") || hash.get("type") === "invite";
}

export async function acceptAuthInviteCallback(location = window.location) {
  const client = requireClient();
  const query = new URLSearchParams(location.search);
  const hash = new URLSearchParams(location.hash.replace(/^#/, ""));
  let failure;

  try {
    if (query.has("code")) {
      ({ error: failure } = await client.auth.exchangeCodeForSession(
        query.get("code"),
      ));
    } else if (hash.get("type") === "invite") {
      const accessToken = hash.get("access_token");
      const refreshToken = hash.get("refresh_token");
      if (!accessToken || !refreshToken) throw new Error("invalid invite callback");
      ({ error: failure } = await client.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      }));
    } else {
      throw new Error("missing invite callback");
    }
    if (failure) throw failure;
  } catch {
    throw new Error("초대 링크가 만료되었거나 올바르지 않습니다.");
  } finally {
    window.history.replaceState({}, document.title, location.pathname);
  }
}
export async function rpc(name, args = {}) {
  const { data, error } = await requireClient().rpc(name, args);
  if (error) {
    const failure = safeError(error);
    if (["save_course_group_classified", "save_course_group_classified_v2"].includes(name) && error.code === "23505") {
      failure.message = "같은 연도·구분·과정·학년·과목에 같은 반 이름의 수업이 있습니다. 비활성 수업도 확인해 주세요.";
      failure.retryable = false;
    }
    throw failure;
  }
  return data;
}
export async function query(request) {
  const { data, error } = await request;
  if (error) throw safeError(error);
  return data;
}
export async function edge(name, body, method = "POST", retried = false) {
  const client = requireClient();
  const { data, error } = await client.functions.invoke(name, {
    method,
    ...(body ? { body } : {}),
  });
  if (error) {
    const status = error.context?.status;
    if (status === 401 && !retried) {
      const refreshed = await client.auth.refreshSession();
      if (!refreshed.error && refreshed.data.session)
        return edge(name, body, method, true);
      await client.auth.signOut({ scope: "local" });
    }
    let payload;
    try {
      payload = await error.context?.json();
    } catch {
      /* Gateway may return no JSON envelope. */
    }
    const failure = safeError(
      { ...payload?.error, requestId: payload?.requestId },
      status,
    );
    if (["ACCOUNT_INACTIVE", "AUTH_INVALID"].includes(failure.code))
      await client.auth.signOut({ scope: "local" });
    throw failure;
  }
  if (!data?.ok)
    throw safeError({ ...data?.error, requestId: data?.requestId });
  return data.data;
}
export async function edgeCsv(name, body, retried = false) {
  const client = requireClient();
  const { data } = await client.auth.getSession();
  if (!data.session) throw safeError({ code: "AUTH_REQUIRED" });
  const response = await fetch(new URL(`/functions/v1/${name}`, url), {
    method: "POST",
    headers: {
      apikey: key,
      Authorization: `Bearer ${data.session.access_token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body || {}),
  });
  if (response.status === 401 && !retried) {
    const refreshed = await client.auth.refreshSession();
    if (!refreshed.error && refreshed.data.session)
      return edgeCsv(name, body, true);
  }
  if (!response.ok) {
    let payload;
    try {
      payload = await response.json();
    } catch {
      /* The gateway can return an empty error body. */
    }
    throw safeError(
      { ...payload?.error, requestId: payload?.requestId },
      response.status,
    );
  }
  if (!response.headers.get("content-type")?.includes("text/csv"))
    throw safeError({ code: "REQUEST_FAILED" });
  return new Blob([await response.text()], { type: "text/csv;charset=utf-8" });
}
export const isManager = (role) => ["owner", "admin"].includes(role);

export function storageUrl(signedUrl) {
  const base = new URL(url);
  const signed = new URL(signedUrl, base);
  if (
    ["127.0.0.1", "localhost"].includes(base.hostname) &&
    signed.hostname === "kong" &&
    signed.pathname.startsWith("/storage/v1/object/sign/")
  )
    return new URL(signed.pathname + signed.search, base).href;
  if (signed.origin !== base.origin)
    throw new Error("파일 다운로드 주소를 확인할 수 없습니다.");
  return signed.href;
}
